<template>
  <breadcrumb-layout
    :org-id="orgId"
    :bread-crumb-items="breadCrumbItems"
    :show-new-goal-button="!team || !team.isArchived"
    :is-archived="kr && kr.isArchived"
    :timeline="goal && goal.timeline"
    :team-id="team && team.id"
    @back="onBack"
  >
    <div class="col-12 col-md-10 col-lg-10 kr-page">
      <div class="kr-details-view no-gutters card-shadow-primary">
        <div class="initiatives-content no-gutters" ref="initiativesContainer">
          <kr-row
            v-if="kr && goal"
            :key="kr.id"
            :kr="kr"
            :orgId="goal && goal.organizationId"
            :goalId="goalId"
            :expand="true"
            :can-toggle-expansion="false"
            :show-details-page-navigator="false"
            :source="source"
            view-type="detail"
            avatar-size="40"
            ref="kr"
            @kr-deleted="navigateToGoalsPage"
            @kr-archived="navigateToGoalsPage"
            @kr-reopened="fetchEvents"
            @moved-kr="reloadRoute"
            @awarded="fetchEvents"
            class="m-0 pr-4 pb-4 pl-0"
          />
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <div class="events-wrapper col-12 col-lg-10">
          <feeds-heading
            class="text-medium feeds-tab-margin"
            :entity-type="ENTITY_TYPE.KEY_RESULT"
            :selected-filter="selectedFilter"
            heading="KR Activity"
          />
          <post-kr-updates
            class="post-kr-updates"
            :is-checkin-collapsed="true"
            :kr="kr"
            @commented="trackCommentEvent"
            @post-checkin="trackCheckinEvent"
            v-if="kr && goal"
            ref="krUpdates"
          />
          <goal-events
            :context="ENTITY_TYPE.KEY_RESULT"
            v-model="events"
            :goal-id="goalId"
            :mentionable-users="usersInScope"
            @awarded="fetchEvents"
            @reply="triggerReply"
            @checkin-updated="refreshKrDetails"
            :selected-activity-filter="selectedFilter"
          ></goal-events>
        </div>
      </div>
    </div>
    <bulk-action-tray />
  </breadcrumb-layout>
</template>

<script>
import { trackEvent } from '@/analytics'
import BreadcrumbLayout from '@/components/BreadcrumbLayout'
import BulkActionTray from '@/components/bulk-actions/BulkActionTray.vue'
import GoalEvents from '@/components/GoalEvents'
import PostKrUpdates from '@/components/kr/PostKrUpdates'
import KrRow from '@/components/KrRow'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME, SOURCE } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { EXT_LOOKUP } from '@/constants/kr'
import { ROUTE_NAMES } from '@/constants/routes'
import EventBus from '@/EventBus'
import BulkActionsMixin from '@/mixins/BulkActionsMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import GoalMixin from '@/mixins/GoalMixin'
import InitiativeDisplayFieldsMixin from '@/mixins/InitiativeDisplayFieldsMixin'
import InitiativesDragAndDropMixin from '@/mixins/InitiativesDragAndDropMixin'
import InitiativesKeyboardNavMixins from '@/mixins/InitiativesKeyboardNavMixins'
import FeedsHeading from '@/components/FeedsHeading'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import SubOrgIdTrackerMixin from '@/mixins/SubOrgIdTrackerMixin'
import TeamMixin from '@/mixins/TeamMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import focusEventNode from '@/utils/focusEventNode'
import getDocumentTitle from '@/utils/getDocumentTitle'
import trackCheckinEvent from '@/utils/goal/trackCheckinEvent'
import {
  getGoalPageRouteConfig,
  getTeamPageRouteConfigFromTeamInfo
} from '@/utils/navigationConfigs'
import removeQueriesFromRoute from '@/utils/removeQueriesFromRoute'
import showAPIError from '@/utils/showAPIError'
import { mapActions } from 'vuex'

export default {
  name: 'KR',
  components: { BulkActionTray, PostKrUpdates, KrRow, GoalEvents, BreadcrumbLayout, FeedsHeading },
  props: ['orgId', 'krId'],
  mixins: [
    InitiativesDragAndDropMixin,
    GoalMixin,
    InitiativesKeyboardNavMixins,
    MembershipsMixin,
    EventBusMixin,
    InitiativeDisplayFieldsMixin,
    TeamMixin,
    SubOrgIdTrackerMixin,
    BulkActionsMixin
  ],
  data() {
    return {
      events: [],
      ENTITY_TYPE,
      source: SOURCE.KR_PAGE,
      isCheckinOverdue: null
    }
  },
  computed: {
    kr() {
      return this.$store.getters.keyResultById(this.krId)
    },
    goalId() {
      return this.kr?.goalId
    },
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    usersInScope() {
      return this.goal ? this.getUsersInScope(this.teamId, this.goal.organizationId) : null
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    teamId() {
      // this is being used by InitiativeDisplayFieldsMixin also
      return this.kr?.teamId
    },
    subOrgId() {
      return this.teamId && this.getClosestOrgLikeTeamId(this.teamId)
    },
    selectedFilter() {
      return this.$store.getters['feedPreference/selectedFilter'](ENTITY_TYPE.KEY_RESULT)
    },
    breadCrumbItems() {
      if (!this.goal || !this.team) return []
      return [
        {
          text: this.team.name,
          to: getTeamPageRouteConfigFromTeamInfo(this.team)
        },
        {
          text: this.goal?.title,
          to: getGoalPageRouteConfig({
            orgId: this.orgId,
            goalId: this.goalId
          }),
          type: ENTITY_TYPE.GOAL
        },
        {
          text: this.kr?.title,
          type: ENTITY_TYPE.KEY_RESULT
        }
      ]
    }
  },
  methods: {
    ...mapActions(['getKREvents', 'getGoal', 'getKR']),
    navigateToGoalsPage() {
      const location = getGoalPageRouteConfig({
        orgId: this.orgId,
        goalId: this.goalId
      })
      this.$router.push(location)
    },
    addInitiative() {
      if (this.isTaskSelected()) {
        this.addTask()
      } else {
        this.$refs.kr.addInitiative()
      }
    },
    async fetchEvents() {
      this.events = await this.getKREvents({
        orgId: this.kr.organizationId,
        goalId: this.goalId,
        krId: this.krId
      })
    },
    attachEvent(event) {
      if (event.keyResultId === this.kr.id) this.events.unshift(event)
    },
    updateHistoryData() {
      EventBus.$emit('UPDATE_RECENT_PAGES_DATA', [
        [
          this.orgId,
          'krId',
          this.krId,
          {
            krTitle: this.kr.title,
            goalTitle: this.goal.title
          }
        ]
      ])
    },
    reloadRoute() {
      this.$router.replace({
        name: ROUTE_NAMES.KR,
        params: {
          orgId: this.kr.organizationId,
          krId: this.kr.id
        }
      })
    },
    pushAnalytics() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.OPEN_PAGE, EVENT_NAME.KR)
    },
    comment() {
      const selectedItem = keymapService.selectedListItem(this.$el)
      if (selectedItem && selectedItem.__vue__) {
        selectedItem.__vue__.showActivityModal({ focusAddCommentOnLoad: true })
        trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ACTIVITY_MODAL_KB, EVENT_NAME.INITIATIVE)
      }
    },
    fetchGoalDetails() {
      if (!this.goal) {
        return this.getGoal({ orgId: this.orgId, goalId: this.goalId })
      }
      return Promise.resolve(this.goal)
    },
    async fetchKrDetails() {
      if (!this.kr) {
        try {
          await this.getKR({ orgId: this.orgId, krId: this.krId })
        } catch (e) {
          showAPIError(e)
        }
      }
    },
    async refreshKrDetails() {
      try {
        await this.getKR({ orgId: this.orgId, krId: this.krId })
      } catch (e) {
        showAPIError(e)
      }
    },
    /**
     *If event id is present in url, scroll to that event
     */
    focusEvent() {
      this.$nextTick(() => focusEventNode(this.$route.query.event))
    },
    handleAwardModalRendering() {
      const {
        query: { section }
      } = this.$route
      if (this.kr && section === 'give-award') {
        this.$refs.kr.showAwardModal()
        removeQueriesFromRoute.call(this, ['section'])
      }
    },
    trackCommentEvent() {
      trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.NEW_COMMENT, EVENT_NAME.KR_PAGE)
    },
    trackCheckinEvent() {
      const isLinkedToGoogleSheet = this.kr.extLookup === EXT_LOOKUP.GOOGLE_SPREADSHEET
      trackCheckinEvent(
        this.$store.getters.user.id,
        this.goal,
        this.kr,
        {
          isLinkedToGoogleSheet,
          source: SOURCE.KR_PAGE_WIDGET,
          isCheckinOverdue: this.isCheckinOverdue
        },
        EVENT_NAME.KR_PAGE
      )
    },
    onBack() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.BACK_BUTTON, EVENT_NAME.KR)
    },
    updateDocumentTitle() {
      const title = this.kr?.title
      if (title) {
        const org = this.getClosestOrgLikeTeam(this.team.id)
        document.title = getDocumentTitle({ title, orgName: org.name })
      }
    },
    triggerReply(event) {
      this.$refs.krUpdates.triggerReply(event)
    }
  },
  async mounted() {
    keymapService.activateCategory('kr', {
      'previous-item': this.selectPreviousItem,
      'next-item': this.selectNextItem,
      'toggle-expansion': () => this.toggleItem(),
      open: this.viewItem,
      'expand-item': this.expandItem,
      'collapse-item': this.collapseItem,
      'edit-initiative': this.editInitiative,
      'done-undone-item': this.doneUndone,
      'add-initiative': this.addInitiative,
      'cancel-editing': this.cancelEditing, // This is coming from GoalMixin
      comment: this.comment,
      'delete-initiative': this.triggerDeleteInitiative
    })
    await this.fetchKrDetails()
    this.fetchDisplayFields()
    if (this.kr) {
      this.fetchGoalDetails().then(this.updateHistoryData)
      this.handleAwardModalRendering()
      await this.fetchEvents()
      this.focusEvent()
    } else {
      EventBus.$emit('SHOW_404')
      return
    }
    this.isCheckinOverdue = this.kr.isCheckinOverdue
    this.pushAnalytics()
    this.listenToEventBus(events.CHECKIN_POSTED, this.attachEvent)
    this.listenToEventBus(events.KR_COMMENT_ADDED, this.attachEvent)
    this.listenToEventBus(events.KR_CLOSED, this.fetchEvents)
    this.listenToEventBus('RELOAD_KR_EVENTS', this.fetchEvents)
    this.updateDocumentTitle()
  },
  beforeRouteUpdate(ignoreThis, ignoreThisToo, next) {
    this.pushAnalytics()
    next()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('kr')
  },
  watch: {
    async goal(current, previous) {
      if (previous && current) {
        this.updateHistoryData()
      }
    },
    async krId() {
      await this.fetchKrDetails()
      this.fetchEvents()
      this.fetchDisplayFields()
      this.updateHistoryData()
      this.updateDocumentTitle()
    },
    async $route(to, from) {
      if (from.query.event !== to.query.event && to.query.event) {
        const event = this.events.find(x => x.id === +to.query.event)
        if (!event) {
          await this.fetchEvents()
        }
        this.focusEvent()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/drag';
@import '~@/styles/styleConfigurations';

.kr-details-view {
  border-radius: 16px;
  margin-top: -15px;
  margin-bottom: 15px;
  background: #ffffff;
  font-size: 0.875rem;
  overflow: visible;
  padding: 25px;
}

.events-wrapper {
  margin: 10px 0 30px;
  position: initial;

  .event-header {
    margin-bottom: 25px;
  }
}

/deep/ .kr .kr-numbers,
/deep/ .kr .progress-indicator {
  font-size: 1rem;
}
/deep/ .kr .progress-indicator {
  width: 43px;
}

/deep/ .kr .initiatives {
  margin-left: 0;
  margin-top: 18px;
}

.post-kr-updates {
  margin-left: -40px;
}

@include media-breakpoint-down(md) {
  /deep/ .kr-details-view {
    box-shadow: none !important;
    border-radius: 0;
  }
  .kr-page,
  .kr-details-view {
    padding: 6px 0 0 0;
  }
  .events-wrapper .event-header {
    margin: 8px 0 24px 0;
  }
  /deep/ .kr-icon-wrapper {
    display: none;
  }
  /deep/ .initiative-row {
    &.editable .summary,
    .summary {
      margin: 0;
    }

    .summary-wrapper {
      padding: 0;
    }

    .initiative-title-readview {
      padding-left: 0;
    }

    .gripper {
      display: none !important;
    }
  }
  .events-wrapper {
    margin: 0 !important;
    padding: 0;
  }
  /deep/ .kr-checkin-measurement-container {
    border-bottom: none;
  }
  .kr-details-view {
    border-bottom: 1px solid $borderPrimary;
  }
  /deep/ .post-kr-updates {
    margin-left: 0;
  }
}
</style>
<style lang="scss">
.initiative-row.editable {
  .cell,
  + .select-list-item .cell,
  + .add-new-row {
    border-top-color: transparent;
  }
}

.initiative-row.editable.expanded {
  .cell,
  + .select-list-item .cell,
  + .add-new-row {
    border-top-color: #edeff1;
  }
}
.feeds-tab-margin {
  .tabs {
    margin-left: 0px;
  }
  margin-bottom: 25px;
}
</style>
